import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/core/Button';
import { Icon } from '@/components/core/Icon';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { Textarea } from '@/components/core/Textarea';
import { AttributeComponent } from '@/components/datamodel/AttributeComponent';
import { useAttributeLabelTranslator } from '@/components/datamodel/hooks/useAttributeLabelTranslator';
import { LayoutProvider } from '@/components/datamodel/hooks/useLayout';
import { useValidationsStore } from '@/components/datamodel/hooks/useValidationsStore';
import { useDataModel } from '@/hooks/useDataModel';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import { restUrl } from '@/services/utils';
import { DataModelAttribute } from '@/types/DataModel';

import {
    buildInitialReviewValues,
    buildReviewRequest,
    CHOOSE_STATUS_VALUE,
    isReviewReadyToSend,
    readItemReviewTemplate,
    resolveTemplateValidations,
    REVIEW_STATUSES,
    type ReviewAttribute,
    reviewAttributeDescription,
    showAttributeDescriptionCallout,
    type TemplateValidationInput,
    translateValidationMessage,
} from './createItemReview';

interface CreateItemReviewFormProps {
    primaryKey: string;
    item?: {
        __validations__?: Record<string, TemplateValidationInput[] | undefined>;
    };
    onDirtyChange?: (isDirty: boolean) => void;
    onSent?: () => void;
}

interface ReviewFormFieldsProps extends CreateItemReviewFormProps {
    attributes: ReviewAttribute[];
    initialValues: Record<string, unknown>;
    onClearDraft: () => void;
}

const ReviewFormFields = ({
    primaryKey,
    attributes,
    initialValues,
    onDirtyChange,
    onSent,
    onClearDraft,
}: ReviewFormFieldsProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const methods = useForm({
        defaultValues: initialValues as { reviewStatus: string; reviewComment: string },
    });
    const { control, handleSubmit, formState: { isDirty, isSubmitting } } = methods;
    const reviewStatus = useWatch({ control, name: 'reviewStatus' }) as string | undefined;
    const reviewComment = useWatch({ control, name: 'reviewComment' }) as string | undefined;
    const attributesRef = useRef(attributes);
    attributesRef.current = attributes;

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => () => {
        try {
            const $rootScope = getRootScope(getInjector());
            if (typeof $rootScope.removeAllCallouts === 'function') {
                $rootScope.removeAllCallouts();
            }
        } catch {
            // No Angular shell.
        }
    }, []);

    const translateAttributeDescription = () => {
        try {
            const $rootScope = getRootScope(getInjector());
            if (typeof $rootScope.translateAttributeDescription === 'function') {
                return (attribute: ReviewAttribute) => $rootScope.translateAttributeDescription(attribute) as string | null | undefined;
            }
        } catch {
            // No Angular shell.
        }
        return undefined;
    };

    const onSubmit = handleSubmit(async (data) => {
        const review = buildReviewRequest(
            primaryKey,
            String(data.reviewStatus ?? ''),
            String(data.reviewComment ?? ''),
            attributesRef.current,
            data,
        );
        try {
            await axios.post(restUrl('reviewerreviews'), review);
            toast.success('SAVED_REVIEW_SUCCESS_MESSAGE');
            onDirtyChange?.(false);
            if (onSent) {
                onSent();
            } else {
                onClearDraft();
            }
        } catch {
            toast.error('SAVED_REVIEW_ERROR_MESSAGE');
        }
    });

    const canSend = isReviewReadyToSend(reviewStatus, reviewComment) && !isSubmitting;
    const describeAttribute = translateAttributeDescription();

    return (
        <FormProvider {...methods}>
            <LayoutProvider layoutName="edit">
                <div className="tw-flex tw-flex-col tw-gap-4" data-testid="create-item-review">
                    <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-12">
                        <div className="md:tw-col-span-4">
                            <label className="tw-mb-1 tw-block tw-text-t-sm tw-text-t-secondary" htmlFor="review-status">
                                {t('ITEM_REVIEW_CREATE')}
                            </label>
                            <Controller
                                name="reviewStatus"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        value={(field.value as string) || CHOOSE_STATUS_VALUE}
                                        onValueChange={(value) => field.onChange(value === CHOOSE_STATUS_VALUE ? '' : value)}
                                    >
                                        <SelectTrigger id="review-status">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={CHOOSE_STATUS_VALUE}>
                                                {t('REVIEW.CHOOSE_STATUS')}
                                            </SelectItem>
                                            {REVIEW_STATUSES.map((option) => (
                                                <SelectItem key={option} value={option}>
                                                    {t(`REVIEW.${option}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>
                        <div className="tw-flex tw-flex-col tw-gap-4 md:tw-col-span-8">
                            {attributes.map((attribute) => {
                                const description = reviewAttributeDescription(attribute, t, describeAttribute);
                                const label = translateAttributeLabel(attribute as DataModelAttribute);
                                return (
                                    <div key={attribute.name}>
                                        <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2">
                                            <label
                                                className="tw-text-t-sm tw-text-t-secondary"
                                                htmlFor={attribute.name}
                                                id={`review_${attribute.name}_label`}
                                            >
                                                {label}
                                            </label>
                                            {description && (
                                                <button
                                                    type="button"
                                                    className="tw-inline-flex tw-items-center"
                                                    aria-label={label}
                                                    data-testid={`review-help-${attribute.name}`}
                                                    onClick={() => showAttributeDescriptionCallout(attribute.name, description)}
                                                >
                                                    <Icon icon={QuestionMarkCircleIcon} variant="info" size="sm" />
                                                </button>
                                            )}
                                        </div>
                                        <AttributeComponent
                                            attribute={attribute as DataModelAttribute}
                                            direction="vertical"
                                            disableUrlSync
                                            fieldPath={attribute.name}
                                            hideLabel
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <label className="tw-mb-1 tw-block tw-text-t-sm tw-text-t-secondary" htmlFor="review-comment">
                            {t('REVIEW.COMMENT')}
                        </label>
                        <Controller
                            name="reviewComment"
                            control={control}
                            render={({ field }) => (
                                <Textarea
                                    {...field}
                                    id="review-comment"
                                    name="review_comment"
                                    rows={6}
                                    value={(field.value as string) || ''}
                                />
                            )}
                        />
                    </div>
                    <div className="tw-flex tw-justify-end">
                        <Button
                            type="button"
                            id="send-review"
                            disabled={!canSend}
                            onClick={onSubmit}
                        >
                            {t('REVIEW.SEND_REVIEW')}
                        </Button>
                    </div>
                </div>
            </LayoutProvider>
        </FormProvider>
    );
};

const CreateItemReviewForm = ({
    primaryKey,
    item,
    onDirtyChange,
    onSent,
}: CreateItemReviewFormProps) => {
    const { dataModel } = useDataModel();
    const validationErrors = useValidationsStore(useShallow((state) => state.validationErrors));
    const validationWarnings = useValidationsStore(useShallow((state) => state.validationWarnings));
    const reviewAttributes = dataModel?.getReviewAttributes?.() as ReviewAttribute[] | undefined;
    const attributes = useMemo(() => reviewAttributes ?? [], [reviewAttributes]);
    const attributeKey = attributes.map((attribute) => attribute.name).join('|');
    const validationSignature = JSON.stringify({
        errors: validationErrors,
        warnings: validationWarnings,
        itemValidations: item?.__validations__ ?? null,
    });
    const signature = `${primaryKey}|${attributeKey}|${validationSignature}`;
    const initialValues = useMemo(() => buildInitialReviewValues({
        attributes,
        template: readItemReviewTemplate(
            item,
            resolveTemplateValidations(
                validationErrors as Record<string, TemplateValidationInput[] | undefined>,
                validationWarnings as Record<string, TemplateValidationInput[] | undefined>,
                item?.__validations__,
                translateValidationMessage,
            ),
        ),
    }), [attributes, item, validationErrors, validationWarnings]);
    const dirtyRef = useRef(false);
    const [formInstance, setFormInstance] = useState({ key: signature, values: initialValues });

    if (!dirtyRef.current && formInstance.key !== signature) {
        setFormInstance({ key: signature, values: initialValues });
    }

    return (
        <ReviewFormFields
            key={formInstance.key}
            primaryKey={primaryKey}
            item={item}
            attributes={attributes}
            initialValues={formInstance.values}
            onDirtyChange={(isDirty) => {
                dirtyRef.current = isDirty;
                onDirtyChange?.(isDirty);
            }}
            onSent={onSent}
            onClearDraft={() => {
                dirtyRef.current = false;
                setFormInstance({
                    key: `${signature}|cleared|${Date.now()}`,
                    values: initialValues,
                });
            }}
        />
    );
};

export { CreateItemReviewForm };
export type { CreateItemReviewFormProps };
